import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Users, TrendingUp, Calendar, AlertTriangle, Phone, UserPlus, Clock, FileText, Handshake } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = { facebook: "Facebook", cold_call: "Cold Call", referral: "Referral", website: "Website", manual: "Manual" };
const STAGE_LABELS = { new_lead: "New Lead", contacted: "Contacted", in_negotiation: "Negotiation", will_call_when_needed: "Will Call When Needed", closed_won: "Won", closed_lost: "Lost" };
const STAGE_COLORS = { new_lead: "bg-blue-100 text-blue-800", contacted: "bg-yellow-100 text-yellow-800", in_negotiation: "bg-orange-100 text-orange-800", will_call_when_needed: "bg-purple-100 text-purple-800", closed_won: "bg-green-100 text-green-800", closed_lost: "bg-gray-100 text-gray-500" };

export default function CRMDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    axios.get(`${API}/crm/dashboard`, { withCredentials: true }).then(r => setData(r.data)).catch(console.error);
  }, []);

  if (!data) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  const pipelineTotal = Object.values(data.pipeline_counts).reduce((a, b) => a + b, 0) || 1;
  const sourceTotal = Object.values(data.source_counts).reduce((a, b) => a + b, 0) || 1;
  const SOURCE_COLORS = { facebook: "#3b82f6", cold_call: "#f59e0b", referral: "#10b981", website: "#8b5cf6", manual: "#6b7280" };

  return (
    <div data-testid="crm-dashboard" className="p-8">
      <h1 className="font-heading text-2xl font-bold text-gray-900 mb-6">CRM Dashboard</h1>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {[
          { icon: Users, label: "Total Clients", value: data.total_clients, to: "/admin/crm/clients" },
          { icon: UserPlus, label: "New Leads", value: data.pipeline_counts.new_lead || 0, to: "/admin/crm/clients?stage=new_lead" },
          { icon: TrendingUp, label: "Won", value: data.pipeline_counts.closed_won || 0, to: "/admin/crm/clients?stage=closed_won" },
          { icon: AlertTriangle, label: "Needs Attention", value: data.needs_attention.length, to: "/admin/crm/clients?attention=true" },
        ].map((card, i) => (
          <div key={i} onClick={() => navigate(card.to)} className="bg-white border border-gray-200 rounded-sm p-5 cursor-pointer hover:-translate-y-0.5 hover:shadow-sm transition-all">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-500">{card.label}</span>
              <card.icon className="w-4 h-4 text-gray-400" />
            </div>
            <p className="font-heading text-3xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Deals (machine ↔ client relationships) */}
      <div data-testid="deals-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { icon: Handshake, label: "Deals ouverts", value: data.open_deals || 0, color: "text-yellow-600" },
          { icon: UserPlus, label: "Nouveaux deals (7j)", value: data.new_deals_week || 0, color: "text-blue-600" },
          { icon: TrendingUp, label: "En négociation", value: data.deal_counts?.negotiating || 0, color: "text-orange-600" },
          { icon: TrendingUp, label: "Gagnés (ce mois)", value: data.closed_won_month || 0, color: "text-green-600" },
        ].map((card, i) => (
          <div key={i} className="bg-yellow-50/40 border border-yellow-200 rounded-sm p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">{card.label}</span>
              <card.icon className={`w-4 h-4 ${card.color}`} />
            </div>
            <p className="font-heading text-3xl font-bold text-gray-900">{card.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Pipeline funnel */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-yellow-600" onClick={() => navigate("/admin/crm/pipeline")}>Sales Pipeline</h3>
          <div className="space-y-3">
            {Object.entries(data.pipeline_counts).map(([stage, count]) => (
              <div key={stage}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-700">{STAGE_LABELS[stage] || stage}</span>
                  <span className="font-mono font-bold text-gray-900">{count}</span>
                </div>
                <div className="h-6 bg-gray-100 rounded-sm overflow-hidden">
                  <div
                    className={`h-full rounded-sm transition-all ${stage === "closed_won" ? "bg-green-500" : stage === "closed_lost" ? "bg-gray-400" : "bg-yellow-400"}`}
                    style={{ width: `${Math.max((count / pipelineTotal) * 100, count > 0 ? 8 : 0)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lead sources */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 cursor-pointer hover:text-yellow-600" onClick={() => navigate("/admin/crm/clients")}>Lead Sources</h3>
          {Object.keys(data.source_counts).length === 0 ? (
            <p className="text-sm text-gray-500">No data yet</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(data.source_counts).map(([src, count]) => (
                <div key={src} className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[src] || "#6b7280" }} />
                  <span className="text-sm text-gray-700 flex-1">{SOURCE_LABELS[src] || src}</span>
                  <span className="font-mono text-sm font-bold text-gray-900">{count}</span>
                  <span className="text-xs text-gray-500 w-12 text-right">{Math.round((count / sourceTotal) * 100)}%</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Needs attention */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle className="w-4 h-4 text-orange-500" />
            <h3 className="font-heading text-lg font-semibold text-gray-900 cursor-pointer hover:text-yellow-600" onClick={() => navigate("/admin/crm/clients")}>Needs Follow-up</h3>
          </div>
          {data.needs_attention.length === 0 ? (
            <p className="text-sm text-gray-500">All clients contacted recently</p>
          ) : (
            <div className="space-y-2">
              {data.needs_attention.map((c) => (
                <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-500">{c.phone || c.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-orange-600 font-medium">
                      {c.last_contact ? `Last: ${new Date(c.last_contact).toLocaleDateString()}` : "Never contacted"}
                    </p>
                    <Badge className={`${STAGE_COLORS[c.pipeline_stage]} border-none text-xs`}>
                      {STAGE_LABELS[c.pipeline_stage] || c.pipeline_stage}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Upcoming appointments */}
        <div className="bg-white border border-gray-200 rounded-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-blue-500" />
            <h3 className="font-heading text-lg font-semibold text-gray-900 cursor-pointer hover:text-yellow-600" onClick={() => navigate("/admin/crm/appointments")}>Upcoming Appointments</h3>
          </div>
          {data.upcoming_appointments.length === 0 ? (
            <p className="text-sm text-gray-500">No upcoming appointments</p>
          ) : (
            <div className="space-y-2">
              {data.upcoming_appointments.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{a.title}</p>
                    <p className="text-xs text-gray-500">{a.client_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono text-gray-700">{a.date} {a.time}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
